import { useEffect, useMemo, useState } from "react";
import { ExceptionReason } from "@api/graphql/generated/generated-types";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  Label,
  Loading,
  Select,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button/Button";
import { useAuth } from "@src/common/hooks/useAuth";
import { useExceptionReasonOptions } from "@src/exceptionReasons/hooks/useExceptionReasonOptions";
import { useForceMarkAsAttempted } from "@src/exceptionReasons/hooks/useForceMarkAsAttempted";
import { ForceAttemptConsequencePanel } from "./components/ForceAttemptConsequencePanel";
import { ForceAttemptStopPicker } from "./components/ForceAttemptStopPicker";
import {
  ForceAttemptModalShipment,
  ForceAttemptModalStop,
  useForceAttemptModalShipment,
} from "./hooks/useForceAttemptModalShipment";

const MODAL_TITLE = "Force Attempt";

interface ForceAttemptModalShellProps {
  isModalOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function ForceAttemptModalShell({
  isModalOpen,
  onClose,
  children,
}: ForceAttemptModalShellProps) {
  return (
    <BaseModal
      isOpen={isModalOpen}
      onClose={onClose}
      closeable
      size="default"
      autoFocus={false}
    >
      <BaseModalHeader>{MODAL_TITLE}</BaseModalHeader>
      <BaseModalBody>{children}</BaseModalBody>
    </BaseModal>
  );
}

interface ReasonOption {
  id: string;
  label: string;
}

// ForceAttemptModalProps is declared below, next to ForceAttemptModal, the
// component whose props it is. TypeScript hoists interfaces, so this
// forward reference in `extends` is valid despite the declaration order.
interface ForceAttemptModalContentProps extends ForceAttemptModalProps {
  shipment: ForceAttemptModalShipment;
}

function ForceAttemptModalContent({
  isModalOpen,
  onClose,
  onSuccess,
  preselectedStopId,
  shipment,
}: ForceAttemptModalContentProps) {
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(
    null
  );
  const {
    exceptionReasons,
    isLoading: isLoadingReasons,
    hasError: hasErrorLoadingReasons,
    errorMessage: reasonsErrorMessage,
  } = useExceptionReasonOptions({ skip: !isModalOpen });
  const { forceMarkAsAttempted, isRecording } = useForceMarkAsAttempted();

  const attemptableStops: ForceAttemptModalStop[] = useMemo(
    () => shipment.stops.filter((stop) => !stop.completedAt),
    [shipment.stops]
  );

  // A preselected stop wins, but only if it is actually attemptable;
  // otherwise a lone open stop needs no decision.
  const preselectedAttemptableStopId: string | null =
    preselectedStopId &&
    attemptableStops.some((stop) => stop.id === preselectedStopId)
      ? preselectedStopId
      : null;

  useEffect(() => {
    if (preselectedAttemptableStopId) {
      setSelectedStopId(preselectedAttemptableStopId);
      return;
    }
    if (attemptableStops.length === 1) {
      setSelectedStopId(attemptableStops[0].id);
      return;
    }
    setSelectedStopId(null);
  }, [preselectedAttemptableStopId, attemptableStops]);

  const reasonOptions: ReasonOption[] = exceptionReasons.map(
    (exceptionReason) => ({
      id: exceptionReason.id,
      label: `${exceptionReason.code} — ${exceptionReason.name}`,
    })
  );
  const selectedReason: ExceptionReason | undefined = exceptionReasons.find(
    (exceptionReason) => exceptionReason.id === selectedReasonId
  );

  function handleConfirm(): void {
    if (!selectedStopId || !selectedReasonId) return;
    forceMarkAsAttempted({
      stopId: selectedStopId,
      exceptionReasonId: selectedReasonId,
    })
      .then(() => {
        onClose();
        onSuccess();
      })
      .catch(() => {
        // The hook already toasted. Swallow here so the modal stays open with
        // the user's selection intact.
      });
  }

  if (isLoadingReasons)
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="flex justify-center py-12">
          <Loading loading />
        </div>
      </ForceAttemptModalShell>
    );

  // Prefer the app's standard translated copy (a permission denial, or a
  // BAD_REQUEST's own server message) over the generic retry line — "try again"
  // is useless advice for a permissions failure. The generic line remains the
  // fallback for a network-only failure, which has no translation.
  if (hasErrorLoadingReasons)
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          {reasonsErrorMessage ??
            "Could not load exception reasons. Try again or close this dialog."}
        </div>
      </ForceAttemptModalShell>
    );

  if (exceptionReasons.length === 0)
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          No exception reasons are configured for this courier. Add one under
          Configuration → Exception Reasons before recording an attempt.
        </div>
      </ForceAttemptModalShell>
    );

  if (attemptableStops.length === 0)
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          Every stop on this order is already completed, so an attempt cannot
          be recorded.
        </div>
      </ForceAttemptModalShell>
    );

  const canSubmit: boolean = !!selectedStopId && !!selectedReasonId;

  return (
    <BaseModal
      isOpen={isModalOpen}
      onClose={onClose}
      closeable
      size="default"
      autoFocus={false}
    >
      <BaseModalHeader>{MODAL_TITLE}</BaseModalHeader>
      <BaseModalBody>
        {/* min-w-0 on the columns keeps a `1fr` track (which defaults to
            min-width:auto) from being widened by long unbreakable content.
            The stop rows do their own shrinking — see ForceAttemptStopPicker. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 [&>*]:min-w-0">
          <div>
            <Label>Stop</Label>
            <ForceAttemptStopPicker
              stops={shipment.stops}
              selectedStopId={selectedStopId}
              onSelectStop={setSelectedStopId}
            />

            <div className="mt-4">
              <Label>Exception reason</Label>
              <Select
                id="force-attempt-reason"
                options={reasonOptions}
                clearable={false}
                value={reasonOptions.filter(
                  (option) => option.id === selectedReasonId
                )}
                onChange={(params) => {
                  if (!params.option) return;
                  setSelectedReasonId(String(params.option.id));
                }}
                placeholder="Select a reason"
              />
            </div>
          </div>

          <ForceAttemptConsequencePanel exceptionReason={selectedReason} />
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <CourieButton onClick={onClose} color="secondary">
            Cancel
          </CourieButton>
          <CourieButton
            onClick={handleConfirm}
            disabled={!canSubmit || isRecording}
            isProcessing={isRecording}
            outline={false}
            color="failure"
          >
            Record Attempt
          </CourieButton>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}

interface ForceAttemptModalProps {
  isModalOpen: boolean;
  shipmentId: string;
  preselectedStopId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ForceAttemptModal({
  isModalOpen,
  shipmentId,
  preselectedStopId,
  onClose,
  onSuccess,
}: ForceAttemptModalProps): JSX.Element | null {
  const { courier } = useAuth();
  const courierId: string | undefined = courier?.courier?.id;

  const { shipment, loading, error, errorMessage } =
    useForceAttemptModalShipment({
      courierId,
      shipmentId,
      skip: !isModalOpen,
    });

  if (!isModalOpen) return null;

  // As above: the standard translated message when there is one, otherwise the
  // generic retry line. The `!shipment` case (a null shipmentById with no error)
  // has no message to translate and correctly falls through to the generic copy.
  if (error || (!loading && !shipment))
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          {errorMessage ?? "Could not load order. Try again or close this dialog."}
        </div>
      </ForceAttemptModalShell>
    );

  if (loading || !shipment)
    return (
      <ForceAttemptModalShell isModalOpen={isModalOpen} onClose={onClose}>
        <div className="flex justify-center py-12">
          <Loading loading />
        </div>
      </ForceAttemptModalShell>
    );

  return (
    <ForceAttemptModalContent
      isModalOpen={isModalOpen}
      shipmentId={shipmentId}
      preselectedStopId={preselectedStopId}
      onClose={onClose}
      onSuccess={onSuccess}
      shipment={shipment}
    />
  );
}

export default ForceAttemptModal;
